import { RouteLocationNormalizedLoaded } from 'vue-router';
import { Event as EventModel } from '~/models/Event';
import { GroupTracker } from '~/models/GroupTracker';
import { SoftwareTracker } from '~/models/SoftwareTracker';
import { Tracker } from '~/models/Tracker';
import { TaggingPlan } from '~/models/TaggingPlan';
import { TaggingPlanEvent } from '~/models/TaggingPlanEvent';
import TextHelper from '~/helpers/TextHelper';
import SoftwareHelper from '~/helpers/SoftwareHelper';

export interface BreadcrumbsItem {
  routeLink: string,
  text: string,
}

export default class BreadcrumbsBuilder {
  private items: Array<BreadcrumbsItem> = [];

  public constructor(breadcrumbsItem?: BreadcrumbsItem) {
    this.items.push(!breadcrumbsItem ? {routeLink: '/', text: 'Dashboard'} : breadcrumbsItem);
  }

  public addItem(route: RouteLocationNormalizedLoaded, text: string): this {
    this.items.push({routeLink: route.fullPath as string, text});
    return this;
  }

  public addManageTrackersItem(): this {
    this.items.push({routeLink: '/trackers', text: 'Manage trackers'});
    return this;
  }

  public addPlanManagementItem(): this {
    this.items.push({ routeLink: '/plan-management', text: 'Plan Management' });
    return this;
  }

  public async addEditEventIdPageItem(route: RouteLocationNormalizedLoaded, text: string = 'Edit event'): Promise<this> {
    const event = await EventModel.findById(parseInt(route.params.eventId as string));
    const eventLabel = this.truncateText(event.label);
    await this.addTrackerIdPageItem(this.createParentRoute(this.createParentRoute(route)),
      'Manage events');
    this.addItem(route, `[${eventLabel}] - ${text}`);
    return this;
  }

  public addManageTemplatesItem(): this {
    this.items.push({routeLink: '/templates', text: 'Template Management'});
    return this;
  }

  public addManageSoftwareItem(): this {
    this.items.push({routeLink: '/software', text: 'Software Management'});
    return this;
  }

  public addManagePlansItem(): this {
    this.items.push({routeLink: '/plan-management', text: 'Plan Management'});
    return this;
  }

  public async addEventIdPageItem(
    route: RouteLocationNormalizedLoaded, text: string = 'Edit event'): Promise<this> {
    const event = await EventModel.findById(parseInt(route.params.eventId as string));
    const eventLabel = this.truncateText(event.label);
    await this.addTrackerIdPageItem(this.createParentRoute(route), 'Manage events');
    this.addItem(route, `[${eventLabel}] - ${text}`);
    return this;
  }

  public async addTemplateEventIdPageItem(
    route: RouteLocationNormalizedLoaded, text: string = 'Edit event'): Promise<this> {
    const event = await EventModel.findById(parseInt(route.params.eventId as string));
    const eventLabel = this.truncateText(event.label);
    await this.addTemplateIdPageItem(this.createParentRoute(this.createParentRoute(route)),
      'Manage events');
    this.addItem(route, `[${eventLabel}] - ${text}`);
    return this;
  }

  public async addEventChildTypeItem(
    route: RouteLocationNormalizedLoaded, text: string, parentText: string,
    tab: string): Promise<this> {
    let parentFullPath = route.fullPath as string;
    for (let i = 0; i < 3; i++) {
      parentFullPath = this.cutLastParamFromPathname(parentFullPath);
    }
    const parentRoute = {
      fullPath: `${parentFullPath}#${tab}`,
      params: route.params,
    } as RouteLocationNormalizedLoaded;
    await this.addEditEventIdPageItem(parentRoute, parentText);
    this.addItem(route, text);
    return this;
  }

  public async addTemplateEventChildTypeItem(route: RouteLocationNormalizedLoaded, text: string, parentText: string, tab: string): Promise<this> {
    let parentFullPath = route.fullPath as string;
    for (let i = 0; i < 3; i++) {
      parentFullPath = this.cutLastParamFromPathname(parentFullPath);
    }
    const parentRoute = {
      fullPath: `${parentFullPath}#${tab}`,
      params: route.params,
    } as RouteLocationNormalizedLoaded;
    await this.addTemplateEventIdPageItem(parentRoute, parentText);
    this.addItem(route, text);
    return this;
  }

  public async addFieldTransformerTypeItem(route: RouteLocationNormalizedLoaded, text: string, cut: number = -1): Promise<this> {
    await this.addEventChildTypeItem(this.createParentRoute(route, cut), 'Transformers', 'Edit event fields', 'fields');
    this.addItem(route, text);
    return this;
  }

  public async addTemplateFieldTransformerTypeItem(route: RouteLocationNormalizedLoaded, text: string, cut: number = -1): Promise<this> {
    await this.addTemplateEventChildTypeItem(this.createParentRoute(route, cut),'Transformers', 'Edit event fields', 'fields');
    this.addItem(route, text);
    return this;
  }

  public async addTrackerIdPageItem(
    route: RouteLocationNormalizedLoaded, text: string): Promise<this> {
    let trackerName = '';
    try {
      const selectedSoftware = SoftwareHelper.getSelectedSoftwareObject();
      if (SoftwareHelper.validateSoftware(selectedSoftware)) {
        const trackerId = parseInt(route.params.id as string);
        if (SoftwareHelper.isAutoV10(selectedSoftware)) {
          const groupTracker = await GroupTracker.findByTrackerId(trackerId);
          trackerName = this.truncateText(groupTracker.tracker.name);
        } else {
          const softwareTracker = await SoftwareTracker.findByTrackerId(trackerId);
          trackerName = this.truncateText(softwareTracker.tracker.name);
        }
      }

      this.addManageTrackersItem();
      this.addItem(route, `[${trackerName}] - ${text}`);
    } catch (error: any) {
      this.addManageTrackersItem();
      this.addItem(route, text);
    }
    return this;
  }

  public async addTemplateIdPageItem(
    route: RouteLocationNormalizedLoaded, text: string): Promise<this> {
    const template = await Tracker.getTemplateById(parseInt(route.params.id as string));
    const templateName = this.truncateText(template.name);
    this.addItem(route, `[${templateName}] - ${text}`);
    return this;
  }

  public async addTemplateChildTypeItem(
    route: RouteLocationNormalizedLoaded, text: string, parentText: string, cut: number = -1): Promise<this> {
    await this.addTemplateIdPageItem(this.createParentRoute(route, cut), text);
    if (parentText) {
      this.addItem(route, parentText);
    }
    return this;
  }

  public async addTrackerChildTypeItem(
    route: RouteLocationNormalizedLoaded, text: string, parentText: string, cut: number = -1): Promise<this> {
    await this.addTrackerIdPageItem(this.createParentRoute(route, cut), parentText);
    this.addItem(route, text);
    return this;
  }

  public getItems(): Array<BreadcrumbsItem> {
    return this.items;
  }

  public createParentRoute(route: RouteLocationNormalizedLoaded, cut: number = -1): RouteLocationNormalizedLoaded {
    return {
      fullPath: this.cutLastParamFromPathname(route.fullPath as string, cut),
      params: route.params,
    } as RouteLocationNormalizedLoaded;
  }

  private cutLastParamFromPathname(path: string, cut: number = -1): string {
    let params = path.split('/');
    if (cut < 0) {
      params = params.slice(0, cut);
    }
    return params.join('/');
  }

  private truncateText(text: string): string {
    return TextHelper.truncate(text, 25);
  }

  public async addTaggingPlanIdPageItem(
    route: RouteLocationNormalizedLoaded, text: string): Promise<this> {
    await TaggingPlan.findById(parseInt(route.params.id as string)).then((plan) => {
      const planName = this.truncateText(plan.name);
      this.addManagePlansItem();
      this.addItem(route, `[${planName}] - ${text}`);
    }).catch(() => {
      this.addManagePlansItem();
      this.addItem(route, text);
    });
    return this;
  }

  public async addTaggingPlanChildTypeItem(
    route: RouteLocationNormalizedLoaded, text: string, parentText: string): Promise<this> {
    await this.addTaggingPlanIdPageItem(this.createParentRoute(route), parentText);
    this.addItem(route, text);
    return this;
  }

  public async addEditTaggingPlanEventIdPageItem(
    route: RouteLocationNormalizedLoaded, text: string = 'Edit event'): Promise<this> {
    const event = await TaggingPlanEvent.findById(parseInt(route.params.eventId as string));
    const eventName = this.truncateText(event.name);
    await this.addTaggingPlanIdPageItem(this.createParentRoute(this.createParentRoute(route)),
      'Manage events');
    this.addItem(route, `[${eventName}] - ${text}`);
    return this;
  }
}
